import type { ActionType, User } from "@autointel/client-api";
import { AiClientMetadata, AiUserProfile, DeviceType } from "@autointel/whiz-models";
import { getFingerprint, setOption } from "@thumbmarkjs/thumbmarkjs";
import { AbstractAiSdk } from "./AbstractAiSdk";
import { VehicleOverlay } from "./inventory-preview/vehicle-overlay.ts";

export class AiSdk extends AbstractAiSdk {
    #hasCredentials: boolean = false;
    #tier1Handover = "";
    #firstTime = false;

    constructor() {
        super();

        if (typeof window !== "undefined" && typeof URLSearchParams !== "undefined") {
            const t1hExpiry = window.localStorage.getItem("t1hx");
            if (!t1hExpiry || Date.now() > parseInt(t1hExpiry)) {
                window.localStorage.removeItem("t1hs");
                window.localStorage.removeItem("t1hx");
                window.sessionStorage.removeItem("firstTime");
            }

            const url = new URL(window.location.href);

            const t1hsQueryParam = url.searchParams.get("t1hs");
            const t1hsLocalStorage = window.localStorage.getItem("t1hs");

            if (t1hsQueryParam && (!t1hsLocalStorage || t1hsQueryParam !== t1hsLocalStorage)) {
                window.localStorage.setItem("t1hs", t1hsQueryParam);
                this.#tier1Handover = t1hsQueryParam;
                window.localStorage.setItem("t1hx", (Date.now() + 60 * 60 * 1000).toString());

                window.sessionStorage.setItem("firstTime", "true");
                this.#firstTime = true;
            } else if (t1hsLocalStorage) {
                this.#tier1Handover = t1hsLocalStorage;
                window.sessionStorage.setItem("firstTime", "false");
            }

            if (t1hsQueryParam) {
                url.searchParams.delete("t1hs");
                window.history.replaceState({}, "", url.toString());
            }

            // ?vids vehicle overlay
            const vids = new URLSearchParams(location.search).get("vids");
            const uid = new URLSearchParams(location.search).get("uid");
            if (!vids || !uid) return;
            const run = () => new VehicleOverlay(vids, uid);
            document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", run) : run();
        }
    }

    async setCredentials(tenant: string, username: string, apiKey: string, whizSecret?: string) {
        await super.setCredentials(tenant, username, apiKey, whizSecret);

        this.#attemptPostPageLoadInteraction();

        if (tenant && username && apiKey) {
            this.#hasCredentials = true;
        }

        if (!whizSecret) {
            return;
        }

        return this.whiz!.getAndSetClientMetadata().then((clientMetadata) => {
            if (!clientMetadata || clientMetadata.tenant !== tenant) {
                throw new Error("Matching tenant not found in client metadata");
            }

            if (clientMetadata.defaultChatOpenOnHandover && this.#tier1Handover) {
                if (clientMetadata.autoOpenWaitSelector) {
                    this.debugLog(
                        "Waiting for selector ",
                        clientMetadata.autoOpenWaitSelector,
                        "for a maximum of ",
                        clientMetadata.autoOpenWaitTimeout ?? 1000,
                        "ms",
                    );
                    this.#enableAutoOpenUiWithWaitSelector(clientMetadata);
                } else if (clientMetadata.autoOpenWaitTimeout) {
                    this.debugLog("Waiting for static delay of ", clientMetadata.autoOpenWaitTimeout, "ms");
                    setTimeout(() => this.#enableAutoOpenUi(clientMetadata), clientMetadata.autoOpenWaitTimeout);
                } else {
                    this.debugLog("Loading immediately");
                    setTimeout(() => this.#enableAutoOpenUi(clientMetadata));
                }
            }
        });
    }

    async setUser({ auto, ...user }: User & { auto?: boolean }): Promise<User | null> {
        if (auto) {
            return this.#setUserAuto();
        }

        if (!user.id) {
            throw new Error("User ID not set");
        }
        //Required for AWS IOT
        user.id = user.id.replace(/[-|]/g, "");

        this.app?.setUser(user);
        this.user = user;
        this.interactionsListener.user = user;
        this.inventory?.setUser(user);
        this.#attemptPostPageLoadInteraction();

        if (!this.whiz) {
            return user;
        }

        const metadata = await this.whiz.getClientMetadata();
        if (metadata?.enrichedProfileEnabled) {
            const enrichedUser: AiUserProfile = await this.whiz.getEnrichedUserProfile(user);
            user = enrichedUser;
        } else {
            user.whizEnabled = metadata?.chatEnabled;
        }

        this.whiz.setUser(user);

        return user;
    }

    #enableAutoOpenUi(clientMetadata: AiClientMetadata) {
        this.debugLog("opening ui");
        this.enableUi(null, {
            title: clientMetadata.clientName,
            defaultOpen: true,
            xOffset: clientMetadata.chatContainerXOffset,
            yOffset: clientMetadata.chatContainerYOffset,
            logoUrl: clientMetadata.chatLogoUrl,
            avatarUrl: clientMetadata.chatAvatarUrl,
        });
    }

    #enableAutoOpenUiWithWaitSelector(clientMetadata: AiClientMetadata, timeWaited: number = 0) {
        if (document.querySelector(clientMetadata.autoOpenWaitSelector)) {
            this.debugLog("selected element found after ", timeWaited, "ms");
            this.#enableAutoOpenUi(clientMetadata);
            return;
        }

        this.debugLog("selected element not found after ", timeWaited, "ms");
        this.debugLog("retrying...");

        if (timeWaited < (clientMetadata.autoOpenWaitTimeout ?? 1000)) {
            setTimeout(() => this.#enableAutoOpenUiWithWaitSelector(clientMetadata, timeWaited + 100), 100);
        } else {
            this.debugLog("max timeout reached, loading immediately");
            this.#enableAutoOpenUi(clientMetadata);
        }
    }

    async #setUserAuto(): Promise<User | null> {
        if (typeof window !== "undefined") {
            const ecid = window.document.cookie
                .split("; ")
                .map((pair) => pair.split("="))
                .filter(([key]) => key.startsWith("AMCV_"))?.[0]?.[1]
                .match(/MCMID%7C([0-9]+)%7C/)?.[1];
            const priorFallback = localStorage.getItem("fallbackEcid");
            const tier1UserId = localStorage.getItem("t1hs");
            const fingerprint = await getFingerprint().catch((e) => {
                console.error("Unable to get tm", e);
            });
            setOption("exclude", ["screen"]);
            const fingerprintNoScreen = await getFingerprint().catch((e) => {
                console.error("Unable to get tmNS", e);
            });

            const userMetadata = {
                t1hs: tier1UserId,
                tm: fingerprint,
                tmNS: fingerprintNoScreen,
            };

            if (priorFallback) {
                // prioritize priorFallback to maintain stable user id
                return this.setUser({ id: priorFallback, ...userMetadata });
            }

            if (ecid) {
                return this.setUser({ id: ecid, ...userMetadata });
            }

            const fallback = crypto.randomUUID().replace(/-/gi, "");
            localStorage.setItem("fallbackEcid", fallback);
            return this.setUser({ id: fallback, ecid, t1hs: tier1UserId });
        } else {
            console.error("setUserAuto() is only compatible in browser");
            return null;
        }
    }

    #attemptPostPageLoadInteraction() {
        if (typeof window === "undefined" || !this.user?.id || !this.#hasCredentials) {
            return;
        }

        const currentTimeMillis = Date.now();

        this.app?.postInteraction({
            // window.navigator.userAgentData is not available on all browsers, critically iOS Safari: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData
            interaction: {
                itemId: window.location.pathname,
                context: {
                    browsingSession: {
                        id: `${this.user.id}-${currentTimeMillis}`,
                        ...((window.navigator as any).userAgentData?.mobile && { deviceType: DeviceType.MOBILE }),
                    },
                    user: {
                        ...this.user,
                        userAgent: window.navigator.userAgent, // TODO: Match known values and move to browsingSession.deviceType
                    },
                    source: {
                        url: window.location.href,
                        name: document.title,
                    },
                    sdkVersion: process.env.PLUGIN_VERSION,
                },
                action: "VIEW" as ActionType,
                subAction: "Page Load",
                timestamp: currentTimeMillis,
            },
        });
    }
}
